import React, { FC, useEffect, useRef } from 'react';
import { CssBaseline } from '@material-ui/core';
import { MuiThemeProvider } from '@material-ui/core/styles';
import { Notifier } from '@airbrake/browser';
import Auth from '@aws-amplify/auth';

import { I18nextProvider } from 'react-i18next';
import { Router, withRouter, RouteComponentProps } from 'react-router-dom';
import { createBrowserHistory } from 'history';

import { AuthProvider } from 'providers';
import { Loaders, ScrollToTop } from 'components';
import Cookies from 'cookies';

import i18n from 'config/i18n';

import theme from 'theme';
import { getSubdomainUrl, isSignedIn, isSignedOut } from 'helpers';
import { isRoot } from 'utilities';
import { useAuth } from 'hooks';

import { ApolloProtected, ApolloPublic } from './providers';
import Protected from './protected/ProtectedIndex';
import ProtectedErrorBoundary from './protected/ProtectedErrorBoundary';
import Public from './public/PublicIndex';
import PublicErrorBoundary from './public/PublicErrorBoundary';

import Connections from './protected/Connections';
import Routes from './protected/Routes';

const { NODE_ENV, REACT_APP_AIRBRAKE_PROJECT_ID, REACT_APP_AIRBRAKE_PROJECT_KEY } = process.env;

const airbrake = new Notifier({
  projectId: parseInt(REACT_APP_AIRBRAKE_PROJECT_ID, 10),
  projectKey: REACT_APP_AIRBRAKE_PROJECT_KEY,
  environment: NODE_ENV,
  instrumentation: {
    console: NODE_ENV !== 'development',
  },
});

airbrake.addFilter(notice => {
  const isDev = notice.context.environment === 'development';
  return isDev ? null : notice;
});

const ValidateUser: FC<RouteComponentProps> = ({ history }) => {
  const { data } = useAuth();
  const signedIn = isSignedIn(data.state);
  const signedOut = isSignedOut(data.state);
  const params = new URLSearchParams(window.location.search);

  // Signed in and visiting base domain
  if (signedIn && isRoot()) {
    if (Cookies.clientSubdomain) {
      window.location.assign(getSubdomainUrl(Cookies.clientSubdomain));
      return null;
    }
  }

  // Direct SSO login from base domain
  if (signedOut && isRoot() && params.get('provider')) {
    Auth.federatedSignIn({ customProvider: params.get('provider') });
    return null;
  }

  const hasMountRan = useRef(false);

  useEffect(() => {
    if (!hasMountRan.current) {
      window.analytics && window.analytics.page();

      history.listen(() => {
        window.analytics && window.analytics.page();
      });

      hasMountRan.current = true;
    }
  });

  if (signedIn) {
    return (
      <ProtectedErrorBoundary airbrake={airbrake}>
        <ApolloProtected>
          <Protected>
            <Connections />
            <Routes />
          </Protected>
        </ApolloProtected>
      </ProtectedErrorBoundary>
    );
  }

  if (signedOut) {
    return (
      <PublicErrorBoundary airbrake={airbrake}>
        <ApolloPublic>
          <Public />
        </ApolloPublic>
      </PublicErrorBoundary>
    );
  }

  return (
    <div style={{ height: '100%' }}>
      <Loaders.Standard />
    </div>
  );
};

const EnhancedUserValidator = withRouter(ValidateUser);

const App: FC = () => (
  <MuiThemeProvider theme={theme}>
    <CssBaseline />
    <I18nextProvider i18n={i18n}>
      <Router history={createBrowserHistory()}>
        <ScrollToTop>
          <AuthProvider>
            <EnhancedUserValidator />
          </AuthProvider>
        </ScrollToTop>
      </Router>
    </I18nextProvider>
  </MuiThemeProvider>
);

export default App;
